import React, { useState, useEffect } from 'react';
import { Container, Tab, Tabs, Card, Table, Button, Form, Modal, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import { ordersAPI, adminAPI, menuAPI, standingOrdersAPI, auditAPI, reportsAPI } from '../../services/api';
import { getTodayStr } from '../../utils/timezone';
import { MENU_CATEGORIES, EMPTY_MENU, CATEGORY_STYLES } from '../../utils/constants';
import AuditLogsTab from './AuditLogsTab';
import WeeklyReportTab from './WeeklyReportTab';
import ItemLocksTab from './ItemLocksTab';
import VersionInfo from './VersionInfo';
import MenuItemsTab from './MenuItemsTab';
import EmailSettingsTab from './EmailSettingsTab';
import DashboardTab from './DashboardTab';

function AdminPortal() {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [menuItems, setMenuItems] = useState(EMPTY_MENU);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [lastOrdersRefresh, setLastOrdersRefresh] = useState(null);
  
  // Modals
  const [showUserModal, setShowUserModal] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);

  // Filters - default to today's date in Chicago timezone
  const [filterDate, setFilterDate] = useState(() => getTodayStr());
  const [filterSlot, setFilterSlot] = useState('');
  
  // Standing Orders
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [standingOrders, setStandingOrders] = useState([]);
  const [editingStandingOrders, setEditingStandingOrders] = useState({});
  const [showStandingSuccessModal, setShowStandingSuccessModal] = useState(false);
  const [standingSuccessDetails, setStandingSuccessDetails] = useState({ customerName: '', updatedSlots: 0 });
  
  // Audit Logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({
    tableName: '',
    userId: '',
    action: '',
    limit: 100,
    changesOnly: false
  });
  
  // Weekly Report
  const [weeklyReport, setWeeklyReport] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh orders every 30 seconds
  useEffect(() => {
    const refreshOrders = async () => {
      try {
        const ordersRes = await ordersAPI.getAll();
        setOrders(ordersRes.data);
        setLastOrdersRefresh(new Date());
      } catch (error) {
        console.error('Failed to refresh orders:', error);
      }
    };

    const interval = setInterval(refreshOrders, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [ordersRes, usersRes, slotsRes, menuRes, daysRes] = await Promise.all([
        ordersAPI.getAll(),
        adminAPI.getUsers(),
        adminAPI.getTimeSlots(),
        menuAPI.getMenuItems(),
        menuAPI.getDaysOfWeek()
      ]);

      setOrders(ordersRes.data);
      setLastOrdersRefresh(new Date());
      setUsers(usersRes.data);
      setTimeSlots(slotsRes.data);
      setMenuItems(menuRes.data);
      setDaysOfWeek(daysRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage({ type: 'danger', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await ordersAPI.updateStatus(orderId, status);
      setMessage({ type: 'success', text: 'Order status updated' });
      loadData();
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to update order status' });
    }
  };

  const handleSaveUser = async (userData) => {
    try {
      if (editingUser) {
        await adminAPI.updateUser(editingUser.id, userData);
        setMessage({ type: 'success', text: 'User updated successfully' });
      } else {
        await adminAPI.createUser(userData);
        setMessage({ type: 'success', text: 'User created successfully' });
      }
      setShowUserModal(false);
      setEditingUser(null);
      loadData();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.error || 'Failed to save user' });
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      await adminAPI.resetPassword(userId, newPassword);
      setMessage({ type: 'success', text: 'Password reset successfully' });
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to reset password' });
    }
  };

  const handleSaveTimeSlot = async (slotData) => {
    try {
      if (editingSlot) {
        await adminAPI.updateTimeSlot(editingSlot.id, slotData);
        setMessage({ type: 'success', text: 'Time slot updated' });
      } else {
        await adminAPI.createTimeSlot(slotData);
        setMessage({ type: 'success', text: 'Time slot created' });
      }
      setShowSlotModal(false);
      setEditingSlot(null);
      loadData();
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to save time slot' });
    }
  };

  // Standing Orders Management
  const loadCustomerStandingOrders = async (userId) => {
    if (!userId) {
      setStandingOrders([]);
      setEditingStandingOrders({});
      return;
    }

    try {
      const response = await standingOrdersAPI.get(userId);
      setStandingOrders(response.data);
      
      // Convert to editing format
      const orders = {};
      response.data.forEach(order => {
        const key = `${order.day_of_week_id}-${order.time_slot_id}-${order.menu_item_id}`;
        orders[key] = order.quantity;
      });
      setEditingStandingOrders(orders);
    } catch (error) {
      console.error('Failed to load standing orders:', error);
      setMessage({ type: 'danger', text: 'Failed to load standing orders' });
    }
  };

  const handleCustomerChange = (userId) => {
    setSelectedCustomer(userId);
    loadCustomerStandingOrders(userId);
  };

  const updateStandingOrderQuantity = (dayId, slotId, itemId, quantity) => {
    const key = `${dayId}-${slotId}-${itemId}`;
    setEditingStandingOrders(prev => ({
      ...prev,
      [key]: Math.max(0, quantity)
    }));
  };

  const saveCustomerStandingOrders = async () => {
    if (!selectedCustomer) return;

    try {
      // Group orders by day/slot for bulk update
      const ordersByDaySlot = {};
      
      Object.entries(editingStandingOrders).forEach(([key, quantity]) => {
        const [dayId, slotId, itemId] = key.split('-').map(Number);
        const dsKey = `${dayId}-${slotId}`;
        
        if (!ordersByDaySlot[dsKey]) {
          ordersByDaySlot[dsKey] = { dayId, slotId, items: [] };
        }
        
        ordersByDaySlot[dsKey].items.push({ menuItemId: itemId, quantity });
      });

      // Save each day/slot combination
      for (const { dayId, slotId, items } of Object.values(ordersByDaySlot)) {
        await standingOrdersAPI.bulkUpdate({
          userId: selectedCustomer,
          dayOfWeekId: dayId,
          timeSlotId: slotId,
          items
        });
      }

      // Get customer name for success modal
      const customer = users.find(u => u.id === parseInt(selectedCustomer));
      const customerName = customer?.location_name || customer?.username || 'Customer';

      // Show success modal
      setStandingSuccessDetails({
        customerName,
        updatedSlots: Object.keys(ordersByDaySlot).length
      });
      setShowStandingSuccessModal(true);

      loadCustomerStandingOrders(selectedCustomer);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to update standing orders' });
    }
  };

  // Audit Logs Management
  const loadAuditLogs = async () => {
    try {
      const response = await auditAPI.getLogs(auditFilters);
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      setMessage({ type: 'danger', text: 'Failed to load audit logs' });
    }
  };

  const handleAuditFilterChange = (field, value) => {
    setAuditFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Weekly Report Management
  const loadWeeklyReport = async () => {
    try {
      const response = await reportsAPI.getWeeklyStandingOrders();
      setWeeklyReport(response.data);
      setMessage({ type: 'success', text: 'Report generated successfully!' });
    } catch (error) {
      console.error('Failed to load weekly report:', error);
      setMessage({ type: 'danger', text: 'Failed to generate report' });
    }
  };

  const openPrintableReport = async () => {
    try {
      await reportsAPI.getWeeklyStandingOrdersPrint();
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to open printable report' });
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filterDate && order.order_date.split('T')[0] !== filterDate) return false;
    if (filterSlot && order.time_slot_id !== parseInt(filterSlot)) return false;
    return true;
  });

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-4 mb-5">
      {message.text && (
        <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
          {message.text}
        </Alert>
      )}

      <Tabs defaultActiveKey="dashboard" className="mb-4">
        <Tab eventKey="dashboard" title="📊 Dashboard">
          <DashboardTab />
        </Tab>
        <Tab eventKey="orders" title="Orders">
          <Card className="fade-in">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>📦 All Orders</span>
              <small className="text-muted">
                🔄 Auto-refreshes every 30s
                {lastOrdersRefresh && (
                  <> | Last: {lastOrdersRefresh.toLocaleTimeString()}</>
                )}
              </small>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Filter by Date</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="date"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                      />
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => setFilterDate(getTodayStr())}
                      >
                        Today
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => setFilterDate('')}
                      >
                        All
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Filter by Time Slot</Form.Label>
                    <Form.Select value={filterSlot} onChange={(e) => setFilterSlot(e.target.value)}>
                      <option value="">All Slots</option>
                      {timeSlots.map(slot => (
                        <option key={slot.id} value={slot.id}>{slot.slot_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Day/Slot</th>
                    <th>Type</th>
                    <th>Items</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => {
                    // Group items by category
                    const groupedItems = Object.fromEntries(MENU_CATEGORIES.map(c => [c, []]));
                    order.items.forEach(item => {
                      const cat = item.category || 'MISC';
                      if (groupedItems[cat]) {
                        groupedItems[cat].push(item);
                      } else {
                        groupedItems.MISC.push(item);
                      }
                    });
                    
                    // Order type badge styling
                    const orderTypeStyles = {
                      standing: { label: '🔄 Standing', bg: 'success' },
                      manual: { label: '✏️ Manual', bg: 'primary' },
                      modified: { label: '⚡ Modified', bg: 'warning' }
                    };
                    const typeStyle = orderTypeStyles[order.order_type] || orderTypeStyles.manual;
                    
                    return (
                      <tr key={order.id}>
                        <td>{order.order_date.split('T')[0]}</td>
                        <td>{order.location_name}</td>
                        <td>{order.day_name} - {order.slot_name}</td>
                        <td>
                          <Badge bg={typeStyle.bg} style={{ fontSize: '0.75rem' }}>
                            {typeStyle.label}
                          </Badge>
                        </td>
                        <td>
                          <small>
                            {MENU_CATEGORIES.map(cat => groupedItems[cat].length > 0 && (
                              <div key={cat}><strong style={{color: CATEGORY_STYLES[cat].color}}>{CATEGORY_STYLES[cat].emoji} {CATEGORY_STYLES[cat].label}:</strong> {groupedItems[cat].map(i => `${i.quantity}x ${i.item_name}`).join(', ')}</div>
                            ))}
                          </small>
                        </td>
                        <td>
                          <span className={`badge ${
                            order.status === 'completed' ? 'bg-success' : 
                            order.status === 'submitted' ? 'bg-primary' :
                            order.status === 'replaced' ? 'bg-secondary' :
                            order.status === 'cancelled' ? 'bg-danger' :
                            'bg-warning'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={order.status}
                            onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                          >
                            <option value="pending">Pending</option>
                            <option value="submitted">Submitted</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="replaced">Replaced</option>
                            <option value="cancelled">Cancelled</option>
                          </Form.Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="users" title="Users">
          <Card className="fade-in">
            <Card.Header>
              👥 User Management
              <Button
                variant="light"
                size="sm"
                className="float-end"
                onClick={() => { setEditingUser(null); setShowUserModal(true); }}
              >
                + Add User
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Location</th>
                    <th>Role</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.location_name}</td>
                      <td>
                        <span className={`badge bg-${user.role === 'admin' ? 'danger' : user.role === 'dashboard' ? 'warning text-dark' : 'info'}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge bg-${user.active ? 'success' : 'secondary'}`}>
                          {user.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => { setEditingUser(user); setShowUserModal(true); }}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="standing-orders" title="Standing Orders">
          <Card className="fade-in">
            <Card.Header>📋 Manage Customer Standing Orders</Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Select Customer</strong></Form.Label>
                    <Form.Select 
                      value={selectedCustomer} 
                      onChange={(e) => handleCustomerChange(e.target.value)}
                    >
                      <option value="">Choose a customer...</option>
                      {users.filter(u => u.role === 'customer').map(user => (
                        <option key={user.id} value={user.id}>
                          {user.location_name} ({user.username})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {selectedCustomer && (
                <>
                  <div className="mb-3">
                    <h5>Standing Orders Grid</h5>
                    <p className="text-muted">Set default quantities for each day and time slot</p>
                  </div>

                  {daysOfWeek.map(day => (
                    <div key={day.id} className="mb-4">
                      <h6 className="text-primary">{day.day_name}</h6>
                      
                      {timeSlots.filter(slot => slot.active).map(slot => (
                        <Card key={slot.id} className="mb-3">
                          <Card.Header className="bg-light">
                            <strong>{slot.slot_name}</strong>
                            <small className="text-muted ms-2">(Cutoff: {slot.cutoff_time})</small>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              {MENU_CATEGORIES.map(category => (
                                <Col md={4} key={category}>
                                  <h6 className="mb-3">{category}</h6>
                                  {menuItems[category]?.map(item => {
                                    const key = `${day.id}-${slot.id}-${item.id}`;
                                    const quantity = editingStandingOrders[key] || 0;
                                    
                                    return (
                                      <div key={item.id} className="d-flex align-items-center mb-2">
                                        <div className="flex-grow-1">
                                          <small>{item.item_name}</small>
                                        </div>
                                        <div className="quantity-control ms-2">
                                          <button 
                                            className="quantity-btn"
                                            onClick={() => updateStandingOrderQuantity(day.id, slot.id, item.id, quantity - 1)}
                                          >
                                            −
                                          </button>
                                          <input
                                            type="number"
                                            className="quantity-input"
                                            value={quantity}
                                            onChange={(e) => updateStandingOrderQuantity(day.id, slot.id, item.id, parseInt(e.target.value) || 0)}
                                            min="0"
                                          />
                                          <button 
                                            className="quantity-btn"
                                            onClick={() => updateStandingOrderQuantity(day.id, slot.id, item.id, quantity + 1)}
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </Col>
                              ))}
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  ))}

                  <div className="text-center mt-4">
                    <Button 
                      variant="primary" 
                      size="lg"
                      onClick={saveCustomerStandingOrders}
                    >
                      💾 Save All Standing Orders
                    </Button>
                  </div>
                </>
              )}

              {!selectedCustomer && (
                <div className="text-center text-muted py-5">
                  <h5>Select a customer to view and edit their standing orders</h5>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="settings" title="Settings">
          <Card className="fade-in mb-4">
            <Card.Header>
              ⚙️ Time Slots
              <Button
                variant="light"
                size="sm"
                className="float-end"
                onClick={() => { setEditingSlot(null); setShowSlotModal(true); }}
              >
                + Add Slot
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped>
                <thead>
                  <tr>
                    <th>Slot Name</th>
                    <th>Cutoff Time</th>
                    <th>Required Time</th>
                    <th>Display Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.map(slot => (
                    <tr key={slot.id}>
                      <td>{slot.slot_name}</td>
                      <td>{slot.cutoff_time}</td>
                      <td>{slot.required_time || '—'}</td>
                      <td>{slot.display_order}</td>
                      <td>
                        <span className={`badge bg-${slot.active ? 'success' : 'secondary'}`}>
                          {slot.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => { setEditingSlot(slot); setShowSlotModal(true); }}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <ItemLocksTab message={message} setMessage={setMessage} />

          <EmailSettingsTab message={message} setMessage={setMessage} />

          <VersionInfo />
        </Tab>

        <Tab eventKey="menu-items" title="Menu Items">
          <MenuItemsTab message={message} setMessage={setMessage} />
        </Tab>

        <Tab eventKey="audit" title="Activity Log">
          <AuditLogsTab
            auditLogs={auditLogs}
            auditFilters={auditFilters}
            users={users}
            timeSlots={timeSlots}
            onFilterChange={handleAuditFilterChange}
            onLoadLogs={loadAuditLogs}
          />
        </Tab>

        <Tab eventKey="reports" title="Weekly Report">
          <WeeklyReportTab
            weeklyReport={weeklyReport}
            onLoadReport={loadWeeklyReport}
            onPrintReport={openPrintableReport}
          />
        </Tab>
      </Tabs>

      <UserModal
        show={showUserModal}
        onHide={() => { setShowUserModal(false); setEditingUser(null); }}
        user={editingUser}
        onSave={handleSaveUser}
        onResetPassword={handleResetPassword}
      />

      <TimeSlotModal
        show={showSlotModal}
        onHide={() => { setShowSlotModal(false); setEditingSlot(null); }}
        slot={editingSlot}
        onSave={handleSaveTimeSlot}
      />

      {/* Standing Order Success Modal */}
      <Modal show={showStandingSuccessModal} onHide={() => setShowStandingSuccessModal(false)} centered>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>✅ Standing Orders Updated!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔄</div>
          <h4>Standing orders updated successfully!</h4>
          <p className="mb-1"><strong>{standingSuccessDetails.customerName}</strong></p>
          <p className="text-muted">{standingSuccessDetails.updatedSlots} day/slot combination(s) updated</p>
          <div className="alert alert-success mt-3 mb-0">
            <strong>⚡ Effective immediately!</strong>
            <br />
            <small>Admin overrides take effect right away.</small>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="success" size="lg" onClick={() => setShowStandingSuccessModal(false)}>
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

// User Modal Component
function UserModal({ show, onHide, user, onSave, onResetPassword }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    locationName: '',
    role: 'customer',
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    streetNumber: '',
    streetName: '',
    city: '',
    state: '',
    zip: '',
    active: true
  });

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        password: '',
        locationName: user.location_name,
        role: user.role,
        email: user.email || '',
        phone: user.phone || '',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        streetNumber: user.street_number || '',
        streetName: user.street_name || '',
        city: user.city || '',
        state: user.state || '',
        zip: user.zip || '',
        active: user.active
      });
    } else {
      setFormData({
        username: '',
        password: '',
        locationName: '',
        role: 'customer',
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
        streetNumber: '',
        streetName: '',
        city: '',
        state: '',
        zip: '',
        active: true
      });
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{user ? 'Edit User' : 'Add User'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              disabled={!!user}
            />
          </Form.Group>

          {!user && (
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Location Name</Form.Label>
            <Form.Control
              type="text"
              value={formData.locationName}
              onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <option value="customer">Customer</option>
              <option value="dashboard">Dashboard (view only)</option>
              <option value="admin">Admin</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </Form.Group>

          <hr />
          <p className="text-muted mb-2"><small><strong>📍 POS Delivery Info</strong> (sent to KitchenMagic)</small></p>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Street #</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.streetNumber}
                  onChange={(e) => setFormData({ ...formData, streetNumber: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={9}>
              <Form.Group className="mb-3">
                <Form.Label>Street Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.streetName}
                  onChange={(e) => setFormData({ ...formData, streetName: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>State</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  maxLength={2}
                  placeholder="IL"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Zip</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  placeholder="60521"
                />
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            />
          </Form.Group>

          {user && (
            <Button
              variant="warning"
              size="sm"
              onClick={() => {
                const newPwd = prompt('Enter new password:');
                if (newPwd) onResetPassword(user.id, newPwd);
              }}
            >
              Reset Password
            </Button>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit">Save</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// Time Slot Modal Component
function TimeSlotModal({ show, onHide, slot, onSave }) {
  const [formData, setFormData] = useState({
    slotName: '',
    cutoffTime: '',
    requiredTime: '',
    displayOrder: 1,
    active: true
  });

  useEffect(() => {
    if (slot) {
      setFormData({
        slotName: slot.slot_name,
        cutoffTime: slot.cutoff_time,
        requiredTime: slot.required_time || '',
        displayOrder: slot.display_order,
        active: slot.active
      });
    } else {
      setFormData({
        slotName: '',
        cutoffTime: '',
        requiredTime: '',
        displayOrder: 1,
        active: true
      });
    }
  }, [slot]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{slot ? 'Edit Time Slot' : 'Add Time Slot'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Slot Name</Form.Label>
            <Form.Control
              type="text"
              value={formData.slotName}
              onChange={(e) => setFormData({ ...formData, slotName: e.target.value })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Cutoff Time</Form.Label>
            <Form.Control
              type="time"
              value={formData.cutoffTime}
              onChange={(e) => setFormData({ ...formData, cutoffTime: e.target.value })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Required Time</Form.Label>
            <Form.Control
              type="time"
              value={formData.requiredTime}
              onChange={(e) => setFormData({ ...formData, requiredTime: e.target.value })}
            />
            <Form.Text className="text-muted">
              When orders are needed (sent to POS). Leave blank if not applicable.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Display Order</Form.Label>
            <Form.Control
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit">Save</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AdminPortal;
